import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { EditorStage } from './editor/EditorStage';
import { MAX_IMAGE_BATCH_BYTES, MAX_IMAGE_FILE_BYTES, prepareImage, type PreparedImage } from './assets/imageImport';
import type { AssetRecord, CollageDocument, TextElement } from '../shared/document';
import { createDocument } from '../shared/documentFactory';
import { grayscaleColor, grayscaleElement } from '../shared/grayscale';
import type { ProjectBundle } from '../shared/ipc';
import { useDocumentStore } from './store/documentStore';
import { useUiStore } from './store/uiStore';
import { binaryAssetRegistry } from './store/binaryAssetRegistry';
import { calculateTemplateSlots, TEMPLATE_CATALOG } from './templates/catalog';
import { renderDocumentImage } from './export/renderImage';
import { systemFontFamily, systemFontId } from '../shared/systemFonts';
import { BUILTIN_FONT_FAMILIES } from '../shared/schemas';
import { heightForCurveChange } from './editor/textWarp';
import { CommitRangeInput } from './components/CommitRangeInput';
import { AiMarkdown } from './components/AiMarkdown';
import type { AiProvider } from '../shared/ipc';
import { formatMessage, getInitialLanguage, saveLanguage, translate, translateText, type Language } from './i18n';

type ToolbarIconName = 'frame' | 'text' | 'export' | 'open' | 'save' | 'saveAs' | 'cancel' | 'fit' | 'photos' | 'layers' | 'templates' | 'pixabay' | 'ai' | 'trash' | 'send';
type GalleryPhoto = { id: string; previewURL: string; largeImageURL: string; tags: string; source: 'pixabay' | 'picsum' };
const DEFAULT_AI_MODELS: Record<AiProvider, string> = { openai: 'gpt-5-mini', perplexity: 'sonar', deepseek: 'deepseek-flash' };
const CANVAS_PRESETS: Record<string, { width: number; height: number; ppi?: number }> = {
  square: { width: 2000, height: 2000 },
  landscape: { width: 3000, height: 2000 },
  portrait: { width: 2000, height: 3000 },
  hd: { width: 1920, height: 1080 },
  story: { width: 1080, height: 1920 },
  a4p: { width: 2480, height: 3508, ppi: 300 },
  a4l: { width: 3508, height: 2480, ppi: 300 },
};

function ToolbarIcon({ name }: { name: ToolbarIconName }): React.JSX.Element {
  return <svg className="action-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {name === 'frame' && <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M12 8v8M8 12h8" /></>}
    {name === 'text' && <><path d="M4 6V4h16v2M12 4v16M8 20h8" /><path d="M8 9h8" /></>}
    {name === 'export' && <><path d="M12 3v12m-5-5 5 5 5-5" /><path d="M5 16v4h14v-4" /></>}
    {name === 'open' && <><path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M12 17V9m-3 3 3-3 3 3" /></>}
    {name === 'save' && <><path d="M4 3h13l3 3v15H4z" /><path d="M8 3v6h8V3M8 21v-8h8v8" /></>}
    {name === 'saveAs' && <><path d="M3 4h12l3 3v13H3z" /><path d="M7 4v5h7V4M7 20v-7h7v7" /><path d="M19 4v6m-3-3h6" /></>}
    {name === 'cancel' && <><circle cx="12" cy="12" r="9" /><path d="m9 9 6 6m0-6-6 6" /></>}
    {name === 'fit' && <><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" /><path d="M8 8l-4-4m12 4 4-4m-4 12 4 4M8 16l-4 4" /></>}
    {name === 'photos' && <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="m21 15-5-5L5 20" /></>}
    {name === 'layers' && <><path d="m12 3 9 5-9 5-9-5z" /><path d="m3 12 9 5 9-5M3 16l9 5 9-5" /></>}
    {name === 'templates' && <><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="5" rx="1" /><rect x="13" y="10" width="8" height="11" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /></>}
    {name === 'pixabay' && <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5M8 13V8h3a2 2 0 0 1 0 4H8" /></>}
    {name === 'ai' && <><path d="M12 3 13.8 8.2 19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="m19 15 .9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9zM5 3l.7 1.8L7.5 5.5l-1.8.7L5 8l-.7-1.8L2.5 5.5l1.8-.7z" /></>}
    {name === 'trash' && <><path d="M4 7h16M10 11v6m4-6v6M5 7l1 14h12l1-14M9 7V4h6v3" /></>}
    {name === 'send' && <><path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" /></>}
  </svg>;
}

async function makeProjectBundle(): Promise<ProjectBundle> {
  const document = useDocumentStore.getState().document;
  const assets: Record<string, Uint8Array> = {};
  const fonts: Record<string, Uint8Array> = {};
  for (const asset of Object.values(document.assets)) {
    const blob = binaryAssetRegistry.get(asset.id);
    if (!blob) throw new Error(`Fotografija ${asset.originalName} ni na voljo za shranjevanje.`);
    assets[asset.id] = new Uint8Array(await blob.arrayBuffer());
  }
  for (const font of Object.values(document.fonts)) {
    const blob = binaryAssetRegistry.get(font.id);
    if (!blob) throw new Error(`Pisava ${font.family} ni na voljo za shranjevanje.`);
    fonts[font.id] = new Uint8Array(await blob.arrayBuffer());
  }
  return { document, assets, fonts };
}

async function restoreProject(bundle: ProjectBundle): Promise<Record<string, string>> {
  binaryAssetRegistry.clear();
  const thumbnails: Record<string, string> = {};
  for (const asset of Object.values(bundle.document.assets)) {
    const bytes = bundle.assets[asset.id];
    if (!bytes) throw new Error(`V projektu manjka fotografija ${asset.originalName}.`);
    const copiedBytes = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const blob = new Blob([copiedBytes], { type: asset.mime });
    binaryAssetRegistry.register(asset.id, blob);
    binaryAssetRegistry.retain(asset.id, 'active-document');
    const bitmap = await createImageBitmap(blob);
    try {
      const scale = Math.min(1, 320 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      if (!context) continue;
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const thumbnail = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (thumbnail) thumbnails[asset.id] = URL.createObjectURL(thumbnail);
    } finally {
      bitmap.close();
    }
  }
  for (const font of Object.values(bundle.document.fonts)) {
    const bytes = bundle.fonts?.[font.id];
    if (!bytes) throw new Error(`V projektu manjka pisava ${font.family}.`);
    const copiedBytes = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const blob = new Blob([copiedBytes], { type: 'font/otf' });
    binaryAssetRegistry.register(font.id, blob);
    binaryAssetRegistry.retain(font.id, 'active-document');
    const url = URL.createObjectURL(blob);
    try {
      const face = await new FontFace(font.family, `url(${url})`, { weight: String(font.weight), style: font.style }).load();
      window.document.fonts.add(face);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  return thumbnails;
}

function App(): React.JSX.Element {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  const languageChangedRef = useRef(false);
  useEffect(() => {
    let active = true;
    void window.photoCollage.getLanguagePreference().then((saved) => {
      if (active && !languageChangedRef.current && saved) {
        setLanguage(saved);
        saveLanguage(saved);
      }
    }).catch(() => undefined);
    return () => { active = false; };
  }, []);
  useEffect(() => { window.document.documentElement.lang = language; }, [language]);
  const [themePreference, setThemePreference] = useState<'system' | 'light' | 'dark'>(() => {
    try {
      const saved = localStorage.getItem('photocollage-theme');
      return saved === 'dark' || saved === 'light' ? saved : 'system';
    } catch { return 'system'; }
  });
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  const theme = themePreference === 'system' ? systemTheme : themePreference;
  const document = useDocumentStore((state) => state.document);
  const [canvasWidthInput, setCanvasWidthInput] = useState(String(document.canvas.width));
  const [canvasHeightInput, setCanvasHeightInput] = useState(String(document.canvas.height));
  const [canvasPpiInput, setCanvasPpiInput] = useState(String(document.canvas.ppi));
  const pastLength = useDocumentStore((state) => state.past.length);
  const futureLength = useDocumentStore((state) => state.future.length);
  const isDirty = useDocumentStore((state) => state.isDirty);
  const undo = useDocumentStore((state) => state.undo);
  const redo = useDocumentStore((state) => state.redo);
  const addElement = useDocumentStore((state) => state.addElement);
  const addFont = useDocumentStore((state) => state.addFont);
  const removeAsset = useDocumentStore((state) => state.removeAsset);
  const selectedId = useUiStore((state) => state.selectedElementId);
  const selectedIds = useUiStore((state) => state.selectedElementIds);
  const cropEditingId = useUiStore((state) => state.cropEditingId);
  const keepAspectRatio = useUiStore((state) => state.keepAspectRatio);
  const zoom = useUiStore((state) => state.zoom);
  const pan = useUiStore((state) => state.pan);
  const textWarpEditingId = useUiStore((state) => state.textWarpEditingId);
  const textEditingId = useUiStore((state) => state.textEditingId);
  const setSelectedElement = useUiStore((state) => state.setSelectedElement);
  const setCropEditing = useUiStore((state) => state.setCropEditing);
  const setKeepAspectRatio = useUiStore((state) => state.setKeepAspectRatio);
  const setTextWarpEditing = useUiStore((state) => state.setTextWarpEditing);
  const setTextEditing = useUiStore((state) => state.setTextEditing);
  const setZoom = useUiStore((state) => state.setZoom);
  const requestFitView = useUiStore((state) => state.requestFitView);
  const updateElement = useDocumentStore((state) => state.updateElement);
  const duplicateElement = useDocumentStore((state) => state.duplicateElement);
  const deleteElement = useDocumentStore((state) => state.deleteElement);
  const reorderElement = useDocumentStore((state) => state.reorderElement);
  const setLayout = useDocumentStore((state) => state.setLayout);
  const updateCanvas = useDocumentStore((state) => state.updateCanvas);
  const beginTransaction = useDocumentStore((state) => state.beginTransaction);
  const commitTransaction = useDocumentStore((state) => state.commitTransaction);
  const cancelTransaction = useDocumentStore((state) => state.cancelTransaction);
  const [activePanel, setActivePanel] = useState<'photos' | 'layers' | 'templates' | 'pixabay' | 'ai'>('photos');
  const pixabayKeyRef = useRef<HTMLInputElement>(null);
  const pixabayQueryRef = useRef<HTMLInputElement>(null);
  const [pixabayResults, setPixabayResults] = useState<GalleryPhoto[]>([]);
  const [pixabayMessage, setPixabayMessage] = useState('');
  const [pixabayBusy, setPixabayBusy] = useState(false);
  const hasAutoLoadedRandomPhotos = useRef(false);
  const [aiProvider, setAiProvider] = useState<AiProvider>(() => {
    try {
      const saved = localStorage.getItem('photocollage-ai-provider');
      return saved === 'openai' || saved === 'perplexity' || saved === 'deepseek' ? saved : 'openai';
    } catch { return 'openai'; }
  });
  const [aiModels, setAiModels] = useState<Record<AiProvider, string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('photocollage-ai-models') ?? '{}') as Partial<Record<AiProvider, unknown>>;
      return Object.fromEntries((Object.keys(DEFAULT_AI_MODELS) as AiProvider[]).map((provider) => {
        const value = saved[provider];
        return [provider, typeof value === 'string' && /^[a-zA-Z0-9._:/-]{1,128}$/.test(value) ? value : DEFAULT_AI_MODELS[provider]];
      })) as Record<AiProvider, string>;
    } catch { return { ...DEFAULT_AI_MODELS }; }
  });
  const [aiKeyDraft, setAiKeyDraft] = useState('');
  const [aiKeysSaved, setAiKeysSaved] = useState<Record<AiProvider, boolean>>({ openai: false, perplexity: false, deepseek: false });
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiAnswers, setAiAnswers] = useState<Record<AiProvider, string>>({ openai: '', perplexity: '', deepseek: '' });
  const [aiMessage, setAiMessage] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => () => { pixabayResults.forEach((hit) => { if (hit.previewURL.startsWith('blob:')) URL.revokeObjectURL(hit.previewURL); }); }, [pixabayResults]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [importMessage, setImportMessage] = useState('');
  const [fontMessage, setFontMessage] = useState('');
  const [systemFonts, setSystemFonts] = useState<string[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'png' | 'jpeg'>('png');
  const [exportWidth, setExportWidth] = useState('');
  const [exportHeight, setExportHeight] = useState('');
  const [exportScale, setExportScale] = useState<1 | 2 | 3 | 4>(1);
  const [exporting, setExporting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const pendingDraft = useRef(false);
  const objectClipboard = useRef<Array<CollageDocument['elements'][number]>>([]);
  const selected = document.elements.find((element) => element.id === selectedId);
  const editingText = document.elements.find((element): element is TextElement => element.id === textEditingId && element.type === 'text');

  useEffect(() => {
    window.document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const update = (event: MediaQueryListEvent): void => setSystemTheme(event.matches ? 'dark' : 'light');
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const cycleThemePreference = (): void => {
    const next = themePreference === 'system' ? 'dark' : themePreference === 'dark' ? 'light' : 'system';
    setThemePreference(next);
    try {
      if (next === 'system') localStorage.removeItem('photocollage-theme');
      else localStorage.setItem('photocollage-theme', next);
    } catch { /* Keep the choice for this session. */ }
  };

  useEffect(() => {
    setCanvasWidthInput(String(document.canvas.width));
    setCanvasHeightInput(String(document.canvas.height));
    setCanvasPpiInput(String(document.canvas.ppi));
  }, [document.canvas.width, document.canvas.height, document.canvas.ppi]);

  useEffect(() => {
    void window.photoCollage.setWindowDocumentName(document.name);
  }, [document.name]);

  useEffect(() => {
    let active = true;
    void window.photoCollage.listSystemFonts().then((families) => { if (active) setSystemFonts(families); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const result = await window.photoCollage.loadStartupProject();
        if (!active) return;
        if (result.status === 'ok') {
          const restoredThumbnails = await restoreProject(result.bundle);
          if (!active) return;
          const restoredDocument = result.bundle.document.canvas.blackAndWhite ? {
            ...result.bundle.document,
            elements: result.bundle.document.elements.map(grayscaleElement),
            canvas: {
              ...result.bundle.document.canvas,
              blackAndWhite: true,
              background: result.bundle.document.canvas.background ? grayscaleColor(result.bundle.document.canvas.background) : null,
              backgroundImageSaturation: 0,
            },
          } : result.bundle.document;
          useDocumentStore.getState().replaceDocument(restoredDocument, result.source === 'saved');
          pendingDraft.current = result.source === 'recovery';
          setThumbnails(restoredThumbnails);
          if (result.warning) setImportMessage(result.warning);
        } else if (result.status === 'empty') {
          const store = useDocumentStore.getState();
          store.replaceDocument(store.document, false);
          pendingDraft.current = true;
          if (result.warning) setImportMessage(result.warning);
        } else if (result.status === 'error') {
          const store = useDocumentStore.getState();
          store.replaceDocument(store.document, false);
          pendingDraft.current = true;
          setImportMessage(result.message);
        }
      } catch {
        const store = useDocumentStore.getState();
        store.replaceDocument(store.document, false);
        pendingDraft.current = true;
        setImportMessage(translate('msg.7935598ff3', language));
      } finally {
        if (active) setIsReady(true);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!isReady) return;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let maxDelayTimer: ReturnType<typeof setTimeout> | undefined;
    let writing = false;
    let changedDuringWrite = false;

    const clearTimers = (): void => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (maxDelayTimer) clearTimeout(maxDelayTimer);
      debounceTimer = undefined;
      maxDelayTimer = undefined;
    };

    const persist = async (): Promise<void> => {
      clearTimers();
      if (writing) { changedDuringWrite = true; return; }
      const current = useDocumentStore.getState();
      if (!current.isDirty && !pendingDraft.current) {
        await window.photoCollage.clearRecovery();
        return;
      }
      writing = true;
      try {
        const bundle = await makeProjectBundle();
        const response = await window.photoCollage.writeRecovery(bundle);
        if (response.status === 'error') setImportMessage(response.message);
      } catch (error) {
        setImportMessage(error instanceof Error ? error.message : translate('msg.draftSaveError', language));
      } finally {
        writing = false;
        if (changedDuringWrite) {
          changedDuringWrite = false;
          debounceTimer = setTimeout(() => void persist(), 250);
        }
      }
    };

    const schedule = (): void => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void persist(), 2000);
      if (!maxDelayTimer) maxDelayTimer = setTimeout(() => void persist(), 30_000);
    };

    const unsubscribe = useDocumentStore.subscribe((state, previous) => {
      if (state.document === previous.document) return;
      if (state.isDirty) {
        pendingDraft.current = true;
        schedule();
      } else {
        pendingDraft.current = false;
        clearTimers();
        void window.photoCollage.clearRecovery();
      }
    });
    if (pendingDraft.current) schedule();

    return () => {
      unsubscribe();
      clearTimers();
      void persist();
    };
  }, [isReady]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      const selectedElementId = useUiStore.getState().selectedElementId;
      const selectedElementIds = useUiStore.getState().selectedElementIds;
      const command = event.ctrlKey || event.metaKey;
      const copySelection = (): void => {
        const ids = new Set(selectedElementIds);
        objectClipboard.current = document.elements.filter((item) => ids.has(item.id)).map((item) => structuredClone(item));
      };
      const pasteClipboard = (): void => {
        if (!objectClipboard.current.length) return;
        const store = useDocumentStore.getState();
        const pastedIds: string[] = [];
        store.beginTransaction();
        for (const source of objectClipboard.current) {
          const id = globalThis.crypto.randomUUID();
          const copy = structuredClone(source);
          copy.id = id;
          copy.name = `${source.name} copy`;
          copy.transform = { ...source.transform, cx: source.transform.cx + 20, cy: source.transform.cy + 20 };
          if (copy.type === 'photo') {
            copy.slotId = null;
            if (copy.assetId && !store.document.assets[copy.assetId]) copy.assetId = null;
          }
          store.addElement(copy);
          pastedIds.push(id);
        }
        store.commitTransaction();
        useUiStore.getState().setSelectedElements(pastedIds);
      };
      if (command && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        useUiStore.getState().setSelectedElements(document.elements.filter((item) => item.visible && !item.locked).map((item) => item.id));
      } else if (command && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      } else if (command && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
      } else if (command && event.key.toLowerCase() === 'c' && selectedElementIds.length && !cropEditingId) {
        event.preventDefault();
        copySelection();
      } else if (command && event.key.toLowerCase() === 'x' && selectedElementIds.length && !cropEditingId) {
        event.preventDefault();
        copySelection();
        const store = useDocumentStore.getState();
        store.beginTransaction();
        for (const id of selectedElementIds) store.deleteElement(id);
        store.commitTransaction();
        useUiStore.getState().setSelectedElement(null);
      } else if (command && event.key.toLowerCase() === 'v' && !cropEditingId) {
        event.preventDefault();
        pasteClipboard();
      } else if (command && event.key.toLowerCase() === 'd' && selectedElementId && !cropEditingId) {
        event.preventDefault();
        copySelection();
        pasteClipboard();
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && selectedElementIds.length && !cropEditingId) {
        event.preventDefault();
        for (const id of selectedElementIds) deleteElement(id);
        setSelectedElement(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cropEditingId, deleteElement, document.elements, redo, setSelectedElement, undo]);
  const startCropEdit = (): void => {
    if (selected?.type !== 'photo' || !selected.assetId || selected.locked) return;
    beginTransaction();
    setCropEditing(selected.id);
  };
  const finishCropEdit = (commit: boolean): void => {
    if (!cropEditingId) return;
    if (commit) commitTransaction();
    else cancelTransaction();
    setCropEditing(null);
  };

  const ingestFiles = async (files: File[], priorErrors: string[] = []): Promise<void> => {
    const errors = [...priorErrors];
    const prepared: PreparedImage[] = [];
    const knownAssets = new Set(Object.keys(useDocumentStore.getState().document.assets));
    let inputBytes = 0;
    let preparedBytes = 0;

    for (const file of files.slice(0, 100)) {
      try {
        if (file.size > MAX_IMAGE_FILE_BYTES) {
          errors.push(`${file.name}: datoteka presega omejitev 50 MiB.`);
          continue;
        }
        if (inputBytes + file.size > MAX_IMAGE_BATCH_BYTES) {
          errors.push('Uvoz serije omejen na 256 MiB. Preostale fotografije lahko uvozite v naslednji seriji.');
          break;
        }
        inputBytes += file.size;
        const image = await prepareImage(file);
        if (knownAssets.has(image.asset.id)) {
          URL.revokeObjectURL(image.thumbnailUrl);
          errors.push(`${file.name}: ${translate('msg.duplicatePhoto', language)}`);
          continue;
        }
        if (knownAssets.size >= 100) {
          URL.revokeObjectURL(image.thumbnailUrl);
          errors.push(translate('msg.photoLimit', language));
          continue;
        }
        if (preparedBytes + image.blob.size > MAX_IMAGE_BATCH_BYTES) {
          URL.revokeObjectURL(image.thumbnailUrl);
          errors.push('Normalizirana serija presega omejitev 256 MiB. Preostale fotografije lahko uvozite pozneje.');
          break;
        }
        knownAssets.add(image.asset.id);
        preparedBytes += image.blob.size;
        prepared.push(image);
      } catch (error) {
        errors.push(error instanceof Error ? error.message : `${file.name}: uvoz ni uspel.`);
      }
    }

    if (prepared.length > 0) {
      const assets: AssetRecord[] = prepared.map(({ asset }) => asset);
      useDocumentStore.getState().addAssets(assets);
      for (const { asset, blob } of prepared) {
        binaryAssetRegistry.register(asset.id, blob);
        binaryAssetRegistry.retain(asset.id, 'active-document');
      }
      setThumbnails((current) => ({
        ...current,
        ...Object.fromEntries(prepared.map(({ asset, thumbnailUrl }) => [asset.id, thumbnailUrl])),
      }));
      setImportMessage(formatMessage('msg.importedPhotos', { count: prepared.length }, language));
      setActivePanel('photos');
    }
    if (errors.length > 0) setImportMessage(errors.map((message) => translateText(message, language)).join(' · '));
  };

  const openImageDialog = async (): Promise<void> => {
    try {
      const result = await window.photoCollage.importImages();
      if (result.status === 'cancelled') return;
      if (result.status === 'error') {
        setImportMessage(result.message);
        return;
      }
      const files = result.files.map((file) => {
        const bytes = Uint8Array.from(file.bytes).buffer;
        return new File([bytes], file.name, { type: file.mime });
      });
      await ingestFiles(files, result.errors);
    } catch {
      setImportMessage(translate('msg.c4b3f91903', language));
    }
  };

  const handleFileDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith('image/') || /\.(jpe?g|png|webp|svg)$/i.test(file.name));
    if (files.length === 0) {
      setImportMessage(translate('msg.473c4ba1aa', language));
      return;
    }
    void ingestFiles(files);
  };

  const saveCurrentProject = async (saveAs = false): Promise<void> => {
    try {
      const response = await window.photoCollage.saveProject(await makeProjectBundle(), saveAs);
      if (response.status === 'cancelled') return;
      if (response.status === 'error') {
        setImportMessage(response.message);
        return;
      }
      pendingDraft.current = false;
      useDocumentStore.getState().replaceDocument(response.document, true);
      setImportMessage(translate('msg.9d2b2c309b', language));
    } catch (error) {
      setImportMessage(error instanceof Error ? error.message : translate('msg.projectSaveError', language));
    }
  };

  const openExistingProject = async (): Promise<void> => {
    if (isDirty && !window.confirm(translate('msg.5acc8eeda7', language))) return;
    const response = await window.photoCollage.openProject();
    if (response.status === 'cancelled') return;
    if (response.status === 'error') {
      setImportMessage(response.message);
      return;
    }
    if (response.status !== 'ok') return;
    try {
      const restoredThumbnails = await restoreProject(response.bundle);
      pendingDraft.current = false;
      objectClipboard.current = [];
      useDocumentStore.getState().replaceDocument(response.bundle.document, true);
      useUiStore.getState().setSelectedElement(null);
      setThumbnails((previous) => {
        Object.values(previous).forEach((url) => URL.revokeObjectURL(url));
        return restoredThumbnails;
      });
      setImportMessage(translate('msg.be9513b22c', language));
    } catch (error) {
      setImportMessage(error instanceof Error ? error.message : translate('msg.projectOpenError', language));
    }
  };

  const cancelCurrentProject = async (): Promise<void> => {
    if (!window.confirm(translate('msg.db71a11bdd', language))) return;
    const response = await window.photoCollage.discardProject();
    if (response.status === 'error') {
      setImportMessage(response.message);
      return;
    }
    binaryAssetRegistry.clear();
    objectClipboard.current = [];
    setThumbnails((previous) => {
      Object.values(previous).forEach((url) => URL.revokeObjectURL(url));
      return {};
    });
    useUiStore.getState().setSelectedElement(null);
    useDocumentStore.getState().replaceDocument(createDocument(), false);
    pendingDraft.current = true;
    setImportMessage(translate('msg.639003877e', language));
  };

  const placeAsset = (asset: AssetRecord): void => {
    const current = useDocumentStore.getState().document;
    const selectedElement = current.elements.find((element) => element.id === useUiStore.getState().selectedElementId);
    if (selectedElement?.type === 'photo') {
      updateElement(selectedElement.id, {
        assetId: asset.id,
        fit: 'cover',
        crop: { zoom: 1, offsetX: 0, offsetY: 0, quarterTurns: 0, flipX: false, flipY: false },
      });
      return;
    }

    const scale = Math.min(1, 760 / asset.width, 600 / asset.height);
    const count = current.elements.filter((element) => element.type === 'photo').length;
    const id = globalThis.crypto.randomUUID();
    addElement({
      id,
      name: asset.originalName,
      type: 'photo',
      assetId: asset.id,
      slotId: null,
      transform: {
        cx: current.canvas.width / 2 + (count % 6) * 24,
        cy: current.canvas.height / 2 + (count % 6) * 24,
        width: Math.max(1, asset.width * scale),
        height: Math.max(1, asset.height * scale),
        rotation: 0,
      },
      visible: true,
      locked: false,
      opacity: 1,
      shadowColor: '#000000', shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0,
      fit: 'cover',
      imageOpacity: 1,
      imageBlur: 0,
      imageBrightness: 1,
      imageSaturation: 1,
      crop: { zoom: 1, offsetX: 0, offsetY: 0, quarterTurns: 0, flipX: false, flipY: false },
      frame: { fill: '#FFFFFF', radius: 0, borderWidth: 0, borderColor: '#FFFFFF' },
    });
    useUiStore.getState().setSelectedElement(id);
  };

  const addEmptyFrame = (): void => {
    const width = Math.min(720, document.canvas.width * 0.7);
    const height = Math.min(480, document.canvas.height * 0.7);
    const count = document.elements.filter((element) => element.type === 'photo').length;
    const offset = (count % 6) * 24;
    const id = globalThis.crypto.randomUUID();
    addElement({
      id,
      name: `Okvir ${document.elements.filter((element) => element.type === 'photo').length + 1}`,
      type: 'photo',
      assetId: null,
      slotId: null,
      transform: {
        cx: document.canvas.width / 2 + offset,
        cy: document.canvas.height / 2 + offset,
        width,
        height,
        rotation: 0,
      },
      visible: true,
      locked: false,
      opacity: 1,
      shadowColor: '#000000', shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0,
      fit: 'cover',
      imageOpacity: 1,
      imageBlur: 0,
      imageBrightness: 1,
      imageSaturation: 1,
      crop: { zoom: 1, offsetX: 0, offsetY: 0, quarterTurns: 0, flipX: false, flipY: false },
      frame: { fill: '#E8EAF0', radius: 12, borderWidth: 0, borderColor: '#FFFFFF' },
    });
    setSelectedElement(id);
  };

  const addText = (): void => {
    const current = useDocumentStore.getState().document;
    const id = globalThis.crypto.randomUUID();
    const count = current.elements.filter((element) => element.type === 'text').length;
    const viewport = window.document.querySelector<HTMLElement>('.stage-viewport');
    const viewWidth = viewport?.clientWidth ?? current.canvas.width;
    const viewHeight = viewport?.clientHeight ?? current.canvas.height;
    const centerX = Math.max(0, Math.min(current.canvas.width, (viewWidth / 2 - pan.x) / zoom));
    const centerY = Math.max(0, Math.min(current.canvas.height, (viewHeight / 2 - pan.y) / zoom));
    addElement({
      id, name: `${translate('ui.text.0878cd61bb', language)} ${count + 1}`, type: 'text', text: translate('ui.text.0431dea1f7', language), fontId: 'builtin-sans',
      fontSize: 120, fontWeight: 400, italic: false, fill: '#202124', align: 'center', lineHeight: 1.2, letterSpacing: 0, curve: 0, slant: 0,
      transform: { cx: centerX, cy: centerY, width: 900, height: 180, rotation: 0 },
      visible: true, locked: false, opacity: 1, shadowColor: '#000000', shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, depth: 0, depthColor: '#000000',
    });
    setSelectedElement(id);
  };

  const importFont = async (): Promise<void> => {
    const result = await window.photoCollage.importFont();
    if (result.status === 'cancelled') return;
    if (result.status === 'error') { setFontMessage(result.message); return; }
    try {
      const bytes = result.file.bytes;
      const hash = await crypto.subtle.digest('SHA-256', bytes);
      const sha256 = Array.from(new Uint8Array(hash), (value) => value.toString(16).padStart(2, '0')).join('');
      const id = globalThis.crypto.randomUUID();
      const extension = result.file.name.toLowerCase().endsWith('.otf') ? 'otf' : 'ttf';
      const family = result.file.name.replace(/\.(?:ttf|otf)$/i, '').replace(/[-_]+/g, ' ').trim() || translate('msg.customFont', language);
      const copied = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      const blob = new Blob([copied], { type: 'font/otf' });
      const url = URL.createObjectURL(blob);
      const face = await new FontFace(family, `url(${url})`).load();
      window.document.fonts.add(face);
      URL.revokeObjectURL(url);
      binaryAssetRegistry.register(id, blob);
      binaryAssetRegistry.retain(id, 'active-document');
      addFont({ id, path: `fonts/${id}.${extension}`, family, weight: 400, style: 'normal', sha256 });
      setFontMessage(formatMessage('msg.fontLoaded', { family }, language));
    } catch {
      setFontMessage(translate('msg.76760bd2ae', language));
    }
  };

  const beginExport = (): void => {
    setExportWidth(String(document.canvas.width));
    setExportHeight(String(document.canvas.height));
    setExportScale(1);
    setExportOpen(true);
  };

  const exportCurrentImage = async (): Promise<void> => {
    const width = Number(exportWidth) * exportScale;
    const height = Number(exportHeight) * exportScale;
    try {
      const lowResolution = document.elements.some((element) => {
        if (element.type !== 'photo' || !element.visible || !element.assetId) return false;
        const asset = document.assets[element.assetId];
        return asset && (asset.width < element.transform.width * width / document.canvas.width || asset.height < element.transform.height * height / document.canvas.height);
      });
      if (lowResolution && !window.confirm(translate('msg.f15f2feb41', language))) return;
      setExporting(true);
      const bytes = await renderDocumentImage(document, width, height, exportFormat);
      const response = await window.photoCollage.exportImage(bytes, exportFormat);
      if (response.status === 'saved') {
        setExportOpen(false);
        setImportMessage(translate('msg.70e5410790', language));
      } else if (response.status === 'error') setImportMessage(response.message);
    } catch (error) {
      setImportMessage(error instanceof Error ? error.message : 'Izvoz ni uspel.');
    } finally {
      setExporting(false);
    }
  };

  const applyTemplate = (templateId: string): void => {
    const template = TEMPLATE_CATALOG.find((item) => item.id === templateId);
    if (!template) return;
    const current = useDocumentStore.getState().document;
    const slots = calculateTemplateSlots(template, current.canvas.width, current.canvas.height);
    const photos = current.elements.filter((element) => element.type === 'photo');
    beginTransaction();
    for (const photo of photos) useDocumentStore.getState().deleteElement(photo.id);
    setLayout({ templateId: template.id, templateVersion: 1, margin: Math.min(current.canvas.width, current.canvas.height) * 0.025, gap: Math.min(current.canvas.width, current.canvas.height) * 0.015, slots });
    for (const [index, slot] of slots.entries()) {
      const prior = photos[index];
      const id = prior?.id ?? globalThis.crypto.randomUUID();
      const asset = prior?.type === 'photo' ? prior.assetId : Object.values(current.assets)[index]?.id ?? null;
      const next = {
        id, name: `Okvir ${index + 1}`, type: 'photo' as const, assetId: asset, slotId: slot.id,
        transform: { cx: slot.x + slot.width / 2, cy: slot.y + slot.height / 2, width: slot.width, height: slot.height, rotation: slot.rotation ?? 0 },
        visible: true, locked: false, opacity: 1, shadowColor: '#000000', shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, fit: 'cover' as const,
        imageOpacity: 1, imageBlur: 0, imageBrightness: 1, imageSaturation: 1,
        crop: { zoom: 1, offsetX: 0, offsetY: 0, quarterTurns: 0 as const, flipX: false, flipY: false },
        frame: { fill: '#E8EAF0', radius: 18, borderWidth: 0, borderColor: '#FFFFFF' },
      };
      useDocumentStore.getState().addElement(next);
    }
    commitTransaction();
    setSelectedElement(null);
  };

  const updateLayoutSpacing = (margin: number, gap: number): void => {
    const current = useDocumentStore.getState().document;
    const layout = current.layout;
    if (!layout) return;
    const template = TEMPLATE_CATALOG.find((item) => item.id === layout.templateId);
    if (!template) return;
    const slots = calculateTemplateSlots(template, current.canvas.width, current.canvas.height, margin, gap);
    const photos = current.elements.filter((element) => element.type === 'photo');
    for (const [index, photo] of photos.entries()) {
      const slot = slots[index];
      if (!slot) continue;
      updateElement(photo.id, { slotId: slot.id, transform: { cx: slot.x + slot.width / 2, cy: slot.y + slot.height / 2, width: slot.width, height: slot.height, rotation: slot.rotation ?? 0 } });
    }
    setLayout({ ...layout, margin, gap, slots });
  };

  const fillEmptyFrames = (): void => {
    const current = useDocumentStore.getState().document;
    const available = Object.values(current.assets).filter((asset) => !current.elements.some((element) => element.type === 'photo' && element.assetId === asset.id));
    const empty = current.elements.filter((element) => element.type === 'photo' && !element.assetId);
    beginTransaction();
    empty.slice(0, available.length).forEach((frame, index) => updateElement(frame.id, { assetId: available[index]!.id, fit: 'cover', crop: { zoom: 1, offsetX: 0, offsetY: 0, quarterTurns: 0, flipX: false, flipY: false } }));
    commitTransaction();
  };

  const convertLayoutToFree = (): void => {
    const current = useDocumentStore.getState().document;
    if (!current.layout) return;
    beginTransaction();
    current.elements.filter((element) => element.type === 'photo' && element.slotId).forEach((element) => updateElement(element.id, { slotId: null }));
    setLayout(null);
    commitTransaction();
  };

  const resizeCanvas = (width: number, height: number, ppi?: number): void => {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 64 || height < 64 || width > 8192 || height > 8192 || width * height > 32_000_000) return;
    const current = useDocumentStore.getState().document;
    beginTransaction();
    updateCanvas({ width, height, ...(ppi === undefined ? {} : { ppi }) });
    if (current.layout) {
      const template = TEMPLATE_CATALOG.find((item) => item.id === current.layout?.templateId);
      if (template) {
        const slots = calculateTemplateSlots(template, width, height, current.layout.margin, current.layout.gap);
        current.elements.filter((element) => element.type === 'photo').forEach((element, index) => {
          const slot = slots[index];
          if (slot) updateElement(element.id, { slotId: slot.id, transform: { cx: slot.x + slot.width / 2, cy: slot.y + slot.height / 2, width: slot.width, height: slot.height, rotation: slot.rotation ?? 0 } });
        });
        setLayout({ ...current.layout, slots });
      }
    }
    commitTransaction();
  };

  const searchPixabay = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const key = pixabayKeyRef.current?.value.trim() ?? '';
    const query = pixabayQueryRef.current?.value.trim() ?? '';
    if (!key) { setPixabayMessage(translate('msg.a333d8ca63', language)); return; }
    if (!query) { setPixabayMessage(translate('msg.b9ac299721', language)); return; }
    setPixabayBusy(true); setPixabayMessage(translate('ui.text.f8d40f1ffc', language));
    try {
      const data = await window.photoCollage.searchPixabay(key, query);
      if (data.status === 'error') throw new Error(data.message);
      const hits = await Promise.all(data.hits.map(async (hit) => {
        const image = await window.photoCollage.downloadPixabayImage(hit.previewURL);
        if (image.status === 'error') return { id: String(hit.id), tags: hit.tags, largeImageURL: hit.largeImageURL, previewURL: '', source: 'pixabay' as const };
        const buffer = image.bytes.buffer.slice(image.bytes.byteOffset, image.bytes.byteOffset + image.bytes.byteLength) as ArrayBuffer;
        return { id: String(hit.id), tags: hit.tags, largeImageURL: hit.largeImageURL, previewURL: URL.createObjectURL(new Blob([buffer], { type: image.mime })), source: 'pixabay' as const };
      }));
      setPixabayResults(hits);
      setPixabayMessage(data.hits.length ? `Zadetki: ${data.hits.length}` : 'Ni zadetkov.');
    } catch (error) { setPixabayResults([]); setPixabayMessage(error instanceof Error ? error.message : 'Iskanje ni uspelo.'); }
    finally { setPixabayBusy(false); }
  };

  const loadRandomPhotos = useCallback(async (): Promise<void> => {
    hasAutoLoadedRandomPhotos.current = true;
    setPixabayBusy(true);
    setPixabayMessage(translate('msg.b1bfbaf7d0', language));
    try {
      const data = await window.photoCollage.getRandomPhotos();
      if (data.status === 'error') throw new Error(data.message);
      const hits = await Promise.all(data.hits.slice(0, 18).map(async (hit) => {
        const image = await window.photoCollage.downloadPixabayImage(hit.previewURL);
        if (image.status === 'error') return null;
        const buffer = image.bytes.buffer.slice(image.bytes.byteOffset, image.bytes.byteOffset + image.bytes.byteLength) as ArrayBuffer;
        return { id: hit.id, tags: `Avtor: ${hit.author} · Lorem Picsum`, largeImageURL: hit.largeImageURL, previewURL: URL.createObjectURL(new Blob([buffer], { type: image.mime })), source: 'picsum' as const };
      }));
      const available = hits.filter((hit): hit is NonNullable<typeof hit> => hit !== null);
      setPixabayResults(available);
      setPixabayMessage(available.length ? translate('msg.7a73aeb772', language) : translate('msg.8062723f6e', language));
    } catch (error) {
      setPixabayResults([]);
      setPixabayMessage(error instanceof Error ? error.message : 'Nalaganje fotografij ni uspelo.');
    } finally { setPixabayBusy(false); }
  }, []);

  useEffect(() => {
    if (activePanel === 'pixabay' && pixabayResults.length === 0 && !hasAutoLoadedRandomPhotos.current) void loadRandomPhotos();
  }, [activePanel, loadRandomPhotos, pixabayResults.length]);

  useEffect(() => {
    if (activePanel !== 'ai') return;
    void window.photoCollage.getAiKeyStatus().then((result) => {
      if (result.status === 'ok') setAiKeysSaved(result.saved);
      else setAiMessage(result.message);
    }).catch(() => setAiMessage(translate('msg.969955f538', language)));
  }, [activePanel]);

  useEffect(() => {
    try { localStorage.setItem('photocollage-ai-provider', aiProvider); } catch { /* Keep the selection for this session. */ }
  }, [aiProvider]);

  useEffect(() => {
    try { localStorage.setItem('photocollage-ai-models', JSON.stringify(aiModels)); } catch { /* Keep model choices for this session. */ }
  }, [aiModels]);

  const saveAiKey = async (): Promise<void> => {
    if (!aiKeyDraft.trim()) { setAiMessage(translate('msg.fb5cdb2ce5', language)); return; }
    setAiBusy(true); setAiMessage(translate('msg.57c20c417a', language));
    const result = await window.photoCollage.saveAiApiKey(aiProvider, aiKeyDraft);
    if (result.status === 'ok') {
      setAiKeysSaved(result.saved); setAiKeyDraft(''); setAiMessage(translate('msg.02621d8019', language));
    } else setAiMessage(result.message);
    setAiBusy(false);
  };

  const clearAiKey = async (): Promise<void> => {
    setAiBusy(true); setAiKeyDraft(''); setAiMessage(translate('msg.285982e54a', language));
    const result = await window.photoCollage.saveAiApiKey(aiProvider, null);
    if (result.status === 'ok') { setAiKeysSaved(result.saved); setAiMessage(translate('msg.565406268d', language)); }
    else setAiMessage(result.message);
    setAiBusy(false);
  };

  const sendAiPrompt = async (): Promise<void> => {
    if (!aiPrompt.trim()) { setAiMessage(translate('msg.ffb99c78e7', language)); return; }
    setAiBusy(true); setAiMessage(translate('msg.a51f94cfdd', language));
    const templateName = translate(TEMPLATE_CATALOG.find((item) => item.id === document.layout?.templateId)?.name ?? 'template.none', language);
    const photoCount = document.elements.filter((element) => element.type === 'photo' && element.assetId).length;
    const textCount = document.elements.filter((element) => element.type === 'text').length;
    const context = formatMessage('msg.aiContext', { name: document.name, width: document.canvas.width, height: document.canvas.height, ppi: document.canvas.ppi, template: templateName, photos: photoCount, texts: textCount }, language);
    const result = await window.photoCollage.sendAiPrompt(aiProvider, aiModels[aiProvider], aiPrompt, context);
    if (result.status === 'ok') { setAiAnswers((answers) => ({ ...answers, [aiProvider]: result.text })); setAiMessage(translate('msg.eee6d3fbc7', language)); }
    else setAiMessage(result.message);
    setAiBusy(false);
  };

  const importPixabayImage = async (result: GalleryPhoto): Promise<void> => {
    setPixabayMessage(translate('msg.f272e2a5d6', language));
    try {
      const response = await window.photoCollage.downloadPixabayImage(result.largeImageURL);
      if (response.status === 'error') throw new Error(response.message);
      const extension = response.mime === 'image/png' ? 'png' : response.mime === 'image/webp' ? 'webp' : 'jpg';
      const buffer = response.bytes.buffer.slice(response.bytes.byteOffset, response.bytes.byteOffset + response.bytes.byteLength) as ArrayBuffer;
      await ingestFiles([new File([buffer], `${result.source}-${result.id}.${extension}`, { type: response.mime })]);
    } catch (error) { setPixabayMessage(error instanceof Error ? error.message : 'Uvoz slike ni uspel.'); }
  };

  const removePhotoAsset = (asset: AssetRecord): void => {
    removeAsset(asset.id);
  };

  if (!isReady) {
    return <main className="loading-screen">{translate('app.loading', language)}</main>;
  }

  return (
    <main className="editor-shell" data-theme={theme}>
      <header className="toolbar">
        <div className="brand"><span className="brand-mark">▧</span>PhotoCollage</div>
        <div className="toolbar-divider" />
        <button type="button" className="toolbar-button" onClick={undo} disabled={pastLength === 0} aria-label={translate('toolbar.undo', language)}>↶</button>
        <button type="button" className="toolbar-button" onClick={redo} disabled={futureLength === 0} aria-label={translate('toolbar.redo', language)}>↷</button>
        <div className="toolbar-divider" />
        <button type="button" className="toolbar-button icon-action primary-action" onClick={addEmptyFrame} data-tooltip={translate('toolbar.addFrame', language)} title={translate('toolbar.addFrame', language)} aria-label={translate('toolbar.addFrame', language)}><ToolbarIcon name="frame" /></button>
        <button type="button" className="toolbar-button icon-action" onClick={addText} data-tooltip={translate('toolbar.addText', language)} title={translate('toolbar.addText', language)} aria-label={translate('toolbar.addText', language)}><ToolbarIcon name="text" /></button>
        <div className="toolbar-spacer" />
        <button type="button" className="toolbar-button icon-action" onClick={() => void openExistingProject()} data-tooltip={translate('toolbar.open', language)} title={translate('toolbar.open', language)} aria-label={translate('toolbar.open', language)}><ToolbarIcon name="open" /></button>
        <button type="button" className="toolbar-button icon-action" onClick={() => void saveCurrentProject()} data-tooltip={translate('toolbar.save', language)} title={translate('toolbar.save', language)} aria-label={translate('toolbar.save', language)}><ToolbarIcon name="save" /></button>
        <button type="button" className="toolbar-button icon-action" onClick={() => void saveCurrentProject(true)} data-tooltip={translate('toolbar.saveAs', language)} title={translate('toolbar.saveAs', language)} aria-label={translate('toolbar.saveAs', language)}><ToolbarIcon name="saveAs" /></button>
        <button type="button" className="toolbar-button icon-action" onClick={beginExport} data-tooltip={translate('toolbar.export', language)} title={translate('toolbar.export', language)} aria-label={translate('toolbar.export', language)}><ToolbarIcon name="export" /></button>
        <button type="button" className="toolbar-button icon-action" onClick={() => void cancelCurrentProject()} data-tooltip={translate('toolbar.cancel', language)} title={translate('toolbar.cancel', language)} aria-label={translate('toolbar.cancel', language)}><ToolbarIcon name="cancel" /></button>
        <div className="toolbar-divider" />
        <button type="button" className="toolbar-button compact" onClick={() => setZoom(zoom / 1.2)} aria-label={translate('toolbar.zoomOut', language)}>−</button>
        <span className="zoom-readout">{Math.round(zoom * 100)}%</span>
        <button type="button" className="toolbar-button compact" onClick={() => setZoom(zoom * 1.2)} aria-label={translate('toolbar.zoomIn', language)}>+</button>
        <button type="button" className="toolbar-button icon-action" onClick={requestFitView} data-tooltip={translate('toolbar.fit', language)} title={translate('toolbar.fit', language)} aria-label={translate('toolbar.fit', language)}><ToolbarIcon name="fit" /></button>
        <button type="button" className="toolbar-button theme-toggle" onClick={cycleThemePreference} aria-label={`Tema: ${themePreference === 'system' ? 'sistemska' : themePreference === 'dark' ? 'temna' : 'svetla'}. Spremeni temo`} title={`Tema: ${themePreference === 'system' ? 'sistemska' : themePreference === 'dark' ? 'temna' : 'svetla'} (klikni za spremembo)`}>{themePreference === 'system' ? '◐' : theme === 'light' ? '☾' : '☀'}</button>
      </header>

      <div className="editor-workspace">
        <aside className="layers-panel">
          <div className="panel-tabs">
            <button type="button" className={`panel-tab-icon${activePanel === 'photos' ? ' is-active' : ''}`} onClick={() => setActivePanel('photos')} data-tooltip={translate('panel.photos', language)} title={translate('panel.photos', language)} aria-label={translate('panel.photos', language)} aria-pressed={activePanel === 'photos'}><ToolbarIcon name="photos" /></button>
            <button type="button" className={`panel-tab-icon${activePanel === 'layers' ? ' is-active' : ''}`} onClick={() => setActivePanel('layers')} data-tooltip={translate('panel.layers', language)} title={translate('panel.layers', language)} aria-label={translate('panel.layers', language)} aria-pressed={activePanel === 'layers'}><ToolbarIcon name="layers" /></button>
            <button type="button" className={`panel-tab-icon${activePanel === 'templates' ? ' is-active' : ''}`} onClick={() => setActivePanel('templates')} data-tooltip={translate('panel.templates', language)} title={translate('panel.templates', language)} aria-label={translate('panel.templates', language)} aria-pressed={activePanel === 'templates'}><ToolbarIcon name="templates" /></button>
            {!__WEB_BUILD__ && <button type="button" className={`panel-tab-icon${activePanel === 'pixabay' ? ' is-active' : ''}`} onClick={() => setActivePanel('pixabay')} data-tooltip={translate('panel.pixabay', language)} title={translate('panel.pixabay', language)} aria-label={translate('panel.pixabay', language)} aria-pressed={activePanel === 'pixabay'}><ToolbarIcon name="pixabay" /></button>}
            {!__WEB_BUILD__ && <button type="button" className={`panel-tab-icon${activePanel === 'ai' ? ' is-active' : ''}`} onClick={() => setActivePanel('ai')} data-tooltip={translate('panel.ai', language)} title={translate('panel.ai', language)} aria-label={translate('panel.ai', language)} aria-pressed={activePanel === 'ai'}><ToolbarIcon name="ai" /></button>}
          </div>
          {!__WEB_BUILD__ && activePanel === 'ai' ? (
            <section className="ai-panel" aria-label={translate('ui.text.2874050463', language)}>
              <label>{translate('ui.text.0d7f7ed97b', language)}
                <select value={aiProvider} onChange={(event) => { setAiProvider(event.target.value as AiProvider); setAiKeyDraft(''); setAiMessage(''); }}>
                  <option value="openai">{translate('ui.text.5ef21a5f2c', language)}</option>
                  <option value="perplexity">{translate('ui.text.585f871356', language)}</option>
                  <option value="deepseek">{translate('ui.text.2b8939917b', language)}</option>
                </select>
              </label>
              <label>{translate('ui.text.c7eb22a300', language)}
                <input type="text" spellCheck={false} maxLength={128} value={aiModels[aiProvider]} onChange={(event) => setAiModels((models) => ({ ...models, [aiProvider]: event.target.value }))} aria-label={`Model za ${aiProvider}`} />
              </label>
              <p className="panel-help ai-model-help">{translate('ui.text.a8373dde45', language)}</p>
              <label>{translate('ui.text.f30cf2a0d7', language)}{aiKeysSaved[aiProvider] ? translate('ui.text.fd7ac63b06', language) : translate('ui.text.2b07c432d9', language)}
                <input type="password" autoComplete="off" spellCheck={false} value={aiKeyDraft} onChange={(event) => setAiKeyDraft(event.target.value)} placeholder={aiKeysSaved[aiProvider] ? translate('ui.text.1604d0fdfa', language) : translate('ui.text.0eea50dd62', language)} aria-label={`${translate('ui.text.98c91f0b82', language)} ${aiProvider}`} />
              </label>
              <div className="ai-key-actions">
                <button type="button" className="toolbar-button icon-action" onClick={() => void saveAiKey()} disabled={aiBusy || !aiKeyDraft.trim()} data-tooltip={translate('ui.text.819d0bf654', language)} title={translate('ui.text.819d0bf654', language)} aria-label={translate('ui.text.819d0bf654', language)}><ToolbarIcon name="save" /></button>
                <button type="button" className="toolbar-button icon-action" onClick={() => void clearAiKey()} disabled={aiBusy || (!aiKeysSaved[aiProvider] && !aiKeyDraft)} data-tooltip={translate('ui.text.16f607eb7a', language)} title={translate('ui.text.16f607eb7a', language)} aria-label={translate('ui.text.16f607eb7a', language)}><ToolbarIcon name="trash" /></button>
              </div>
              <div className="ai-prompt-heading"><label htmlFor="ai-prompt">{translate('ui.text.15b7defe02', language)}</label><button type="button" className="toolbar-button icon-action" onClick={() => setAiPrompt('')} disabled={!aiPrompt} data-tooltip={translate('ui.text.c888a1e84f', language)} title={translate('ui.text.c888a1e84f', language)} aria-label={translate('ui.text.c888a1e84f', language)}><ToolbarIcon name="trash" /></button></div>
              <textarea id="ai-prompt" className="ai-prompt-input" rows={6} maxLength={20000} value={aiPrompt} onChange={(event) => setAiPrompt(event.target.value)} onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  if (!aiBusy && aiPrompt.trim()) void sendAiPrompt();
                }
              }} placeholder={translate('ui.text.1e3dc48dd3', language)} />
              <button type="button" className="import-button ai-send" onClick={() => void sendAiPrompt()} disabled={aiBusy || !aiPrompt.trim() || !/^[a-zA-Z0-9._:/-]{1,128}$/.test(aiModels[aiProvider].trim())}>{aiBusy ? translate('ui.text.bd4f12f466', language) : translate('ui.text.0d74a33b73', language)}</button>
              <p className="panel-help">{translate('ui.text.02c14a01dd', language)}</p>
              {aiMessage && <p className="import-message" role="status">{translateText(aiMessage, language)}</p>}
              {aiAnswers[aiProvider] && <article className="ai-answer" aria-live="polite"><AiMarkdown text={aiAnswers[aiProvider]} /></article>}
            </section>
          ) : !__WEB_BUILD__ && activePanel === 'pixabay' ? (
            <>
              {pixabayMessage && <p className="import-message" role="status">{translateText(pixabayMessage, language)}</p>}
              <button className="import-button random-refresh" type="button" onClick={() => void loadRandomPhotos()} disabled={pixabayBusy}>{pixabayBusy ? translate('ui.text.f566870ef4', language) : translate('ui.text.32d3bbf48c', language)}</button>
              {pixabayResults.length > 0 && <ul className="photo-library pixabay-results">{pixabayResults.map((result) => <li key={`${result.source}-${result.id}`}><button type="button" className="photo-library-item" onClick={() => void importPixabayImage(result)} title={`Uvozi: ${result.tags}`}><img src={result.previewURL} alt={result.tags} loading="lazy" /><span className="photo-tooltip">{result.tags}</span></button></li>)}</ul>}
              <form className="pixabay-search" onSubmit={(event) => void searchPixabay(event)}>
                <div className="panel-help">{translate('ui.text.c7dd8f99d2', language)}</div>
                <label>{translate('ui.text.825ab0e873', language)}
                  <input ref={pixabayKeyRef} type="password" autoComplete="off" defaultValue={(() => { try { return localStorage.getItem('photocollage-pixabay-key') ?? ''; } catch { return ''; } })()} placeholder={translate('ui.text.0eea50dd62', language)} onChange={(event) => { try { localStorage.setItem('photocollage-pixabay-key', event.target.value); } catch { /* Keep the key for this session. */ } }} />
                </label>
                <label>{translate('ui.text.2f1c9ad842', language)}
                  <input ref={pixabayQueryRef} type="search" placeholder={translate('ui.text.92f24239c6', language)} />
                </label>
                <button className="import-button" type="submit" disabled={pixabayBusy}>{pixabayBusy ? translate('ui.text.f8d40f1ffc', language) : translate('ui.text.b9c0e09a86', language)}</button>
              </form>
              <p className="panel-help">{translate('ui.text.9ccfcce51e', language)}</p>
            </>
          ) : activePanel === 'templates' ? (
            <div className="template-panel-scroll" role="region" aria-label={translate('ui.text.733a6e75b1', language)} tabIndex={0}>
              <p className="panel-help">{formatMessage('template.layoutHint', { count: TEMPLATE_CATALOG.length }, language)}</p>
              {document.layout && <section className="template-options">
                <label>{translate('ui.text.227fff1fe5', language)} <input type="number" min="0" max={Math.floor(Math.min(document.canvas.width, document.canvas.height) / 3)} step="1" value={Math.round(document.layout.margin)} onFocus={beginTransaction} onBlur={commitTransaction} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value) && value >= 0) updateLayoutSpacing(value, document.layout?.gap ?? 0); }} /></label>
                <label>{translate('ui.text.fd3eb8500e', language)} <input type="number" min="0" max={Math.floor(Math.min(document.canvas.width, document.canvas.height) / 3)} step="1" value={Math.round(document.layout.gap)} onFocus={beginTransaction} onBlur={commitTransaction} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value) && value >= 0) updateLayoutSpacing(document.layout?.margin ?? 0, value); }} /></label>
                <button type="button" className="toolbar-button" onClick={fillEmptyFrames}>{translate('ui.text.140f73cc91', language)}</button>
                <button type="button" className="toolbar-button" onClick={convertLayoutToFree}>{translate('ui.text.7093ba7411', language)}</button>
              </section>}
              <ul className="template-list">{TEMPLATE_CATALOG.map((template) => <li key={template.id}><button type="button" className={`template-item${document.layout?.templateId === template.id ? ' is-active' : ''}`} aria-pressed={document.layout?.templateId === template.id} onClick={() => applyTemplate(template.id)}><span className="template-preview">{template.cells.map((cell, index) => <i key={index} style={{ left: `${cell.x / 6 * 100}%`, top: `${cell.y / 6 * 100}%`, width: `${cell.width / 6 * 100}%`, height: `${cell.height / 6 * 100}%`, transform: cell.rotation ? `rotate(${cell.rotation}deg)` : undefined }} />)}</span><span>{translate(template.name, language)}</span></button></li>)}</ul>
            </div>
          ) : activePanel === 'photos' ? (
            <>
              <button type="button" className="import-button" onClick={() => void openImageDialog()}>{translate('ui.text.998f592d09', language)}</button>
              <p className="panel-help">{translate('ui.text.f9b7ba00b8', language)}</p>
              {Object.keys(document.assets).length === 0 ? (
                <p className="empty-panel">{translate('ui.text.05b4bbc29e', language)}</p>
              ) : (
                <ul className="photo-library">
                  {Object.values(document.assets).map((asset) => (
                    <li key={asset.id} className="photo-library-entry">
                      <button type="button" className="photo-library-item" onClick={() => placeAsset(asset)} aria-label={asset.originalName}>
                        {thumbnails[asset.id] ? <img src={thumbnails[asset.id]} alt="" /> : <span className="photo-placeholder">▧</span>}
                        <span className="photo-tooltip" role="tooltip">{asset.originalName}</span>
                      </button>
                      <button type="button" className="photo-remove-button" onClick={() => removePhotoAsset(asset)} aria-label={`Odstrani ${asset.originalName} iz projekta`} title={translate('ui.text.494ede424f', language)}>×</button>
                    </li>
                  ))}
                </ul>
              )}
              {importMessage && <p className="import-message" role="status">{translateText(importMessage, language)}</p>}
            </>
          ) : (
            <>
              <div className="panel-heading">{translate('ui.text.05cbfa172a', language)} <span>{document.elements.length}</span></div>
              {document.elements.length === 0 ? (
                <p className="empty-panel">{translate('ui.text.826518435e', language)}</p>
              ) : (
                <ul className="layer-list">
                  {[...document.elements].reverse().map((element) => (
                    <li key={element.id}>
                      <div className={`layer-row ${selectedIds.includes(element.id) ? 'is-selected' : ''}`}>
                        <button type="button" className="layer-item" onClick={(event) => {
                          const current = useUiStore.getState().selectedElementIds;
                          if (event.ctrlKey || event.metaKey) useUiStore.getState().setSelectedElements(current.includes(element.id) ? current.filter((id) => id !== element.id) : [...current, element.id], element.id);
                          else if (event.shiftKey) {
                            const order = document.elements.map((item) => item.id);
                            const from = order.indexOf(selectedId ?? element.id), to = order.indexOf(element.id);
                            useUiStore.getState().setSelectedElements(order.slice(Math.min(from, to), Math.max(from, to) + 1));
                          } else setSelectedElement(element.id);
                        }}>
                      <span className="layer-icon">{element.type === 'photo' ? '▧' : 'T'}</span>
                          <span className="layer-name">{element.name}</span>
                        </button>
                        <button type="button" className="layer-toggle" title={translate(element.visible ? 'ui.layer.hide' : 'ui.layer.show', language)} aria-label={translate(element.visible ? 'ui.layer.hide' : 'ui.layer.show', language)} onClick={() => updateElement(element.id, { visible: !element.visible })}>{element.visible ? '◉' : '○'}</button>
                        <button type="button" className="layer-toggle" title={translate(element.locked ? 'ui.layer.unlock' : 'ui.layer.lock', language)} aria-label={translate(element.locked ? 'ui.layer.unlock' : 'ui.layer.lock', language)} onClick={() => updateElement(element.id, { locked: !element.locked })}>{element.locked ? '▣' : '◇'}</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </aside>

        <section className="canvas-area" aria-label={translate('ui.text.be0328ee39', language)} onDragOver={(event) => event.preventDefault()} onDrop={handleFileDrop}>
          <EditorStage />
          {editingText && <textarea
            className="canvas-text-editor"
            aria-label={translate('ui.text.50b01ea960', language)}
            autoFocus
            value={editingText.text}
            onChange={(event) => updateElement(editingText.id, { text: event.target.value })}
            onBlur={() => { commitTransaction(); setTextEditing(null); }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') { event.preventDefault(); cancelTransaction(); setTextEditing(null); }
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); commitTransaction(); setTextEditing(null); }
            }}
            style={{
              left: pan.x + (editingText.transform.cx - editingText.transform.width / 2) * zoom,
              top: pan.y + (editingText.transform.cy - editingText.transform.height / 2) * zoom,
              width: editingText.transform.width * zoom,
              height: editingText.transform.height * zoom,
              transform: `rotate(${editingText.transform.rotation}deg)`,
              fontSize: editingText.fontSize * zoom,
              fontFamily: document.fonts[editingText.fontId]?.family ?? BUILTIN_FONT_FAMILIES[editingText.fontId] ?? systemFontFamily(editingText.fontId) ?? 'sans-serif',
              fontWeight: editingText.fontWeight,
              fontStyle: editingText.italic ? 'italic' : 'normal',
              color: editingText.fill,
              backgroundColor: (() => {
                const match = /^#([\da-f]{6})$/i.exec(editingText.fill);
                if (!match?.[1]) return '#ffffff';
                const value = Number.parseInt(match[1], 16);
                const luminance = (0.2126 * ((value >> 16) & 255) + 0.7152 * ((value >> 8) & 255) + 0.0722 * (value & 255)) / 255;
                return luminance > 0.55 ? '#242630' : '#ffffff';
              })(),
              textAlign: editingText.align,
              lineHeight: editingText.lineHeight,
              letterSpacing: `${editingText.letterSpacing * zoom}px`,
            }}
          />}
          <div className="canvas-hint">{translate('ui.text.0c97ea2e43', language)}</div>
        </section>

        <aside className="inspector-panel">
          <div className="panel-heading">{translate('ui.text.a267e2a574', language)}</div>
          {selected ? (
            <div className="property-list">
              <label>{translate('ui.text.8f6b9783da', language)} <input value={selected.name} readOnly /></label>
              <label>{translate('ui.text.d3e10a1c9a', language)} <input value={selected.type === 'photo' ? translate('ui.text.41fe0e87a3', language) : translate('ui.text.0878cd61bb', language)} readOnly /></label>
              <div className="property-grid">
                {(['cx', 'cy', 'width', 'height'] as const).map((field) => <label key={field}>{({ cx: 'X', cy: 'Y', width: translate('ui.text.fe83ef0113', language), height: translate('ui.text.81f5e26fa1', language) })[field]} <input type="number" min={field === 'width' || field === 'height' ? 1 : undefined} step="1" value={Math.round(selected.transform[field])} disabled={selected.locked} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value) && (field === 'cx' || field === 'cy' || value > 0)) updateElement(selected.id, { transform: { ...selected.transform, [field]: value } }); }} /></label>)}
              </div>
              <label>{translate('ui.text.d765ecbce6', language)} <input type="number" step="1" value={selected.transform.rotation} disabled={selected.locked} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value)) updateElement(selected.id, { transform: { ...selected.transform, rotation: value } }); }} /></label>
              <label className="checkbox-label"><input type="checkbox" checked={keepAspectRatio} disabled={selected.locked} onChange={(event) => setKeepAspectRatio(event.target.checked)} /> {translate('ui.text.475ac854ab', language)}</label>
              <div className="layer-actions">
                <button type="button" className="toolbar-button" onClick={() => { const id = duplicateElement(selected.id); if (id) setSelectedElement(id); }}>{translate('ui.text.2871bf864d', language)}</button>
                <button type="button" className="toolbar-button" onClick={() => { const index = document.elements.findIndex((item) => item.id === selected.id); reorderElement(selected.id, Math.min(document.elements.length - 1, index + 1)); }}>{translate('ui.text.53654fc821', language)}</button>
                <button type="button" className="toolbar-button" onClick={() => { const index = document.elements.findIndex((item) => item.id === selected.id); reorderElement(selected.id, Math.max(0, index - 1)); }}>{translate('ui.text.e7f19b2031', language)}</button>
                <button type="button" className="toolbar-button" onClick={() => { deleteElement(selected.id); setSelectedElement(null); }}>{translate('ui.text.0cadab433b', language)}</button>
              </div>
              {selected.type === 'text' && (
                <section className="crop-controls" aria-label={translate('ui.text.52379c79a4', language)}>
                  <div className="panel-heading">{translate('ui.text.0878cd61bb', language)}</div>
                  <label>{translate('ui.text.a3482a1ece', language)} <textarea rows={4} maxLength={10000} value={selected.text} onFocus={beginTransaction} onBlur={commitTransaction} onChange={(event) => updateElement(selected.id, { text: event.target.value })} /></label>
                  <label>{translate('ui.text.6abe3762d6', language)} <select value={selected.fontId} onChange={(event) => updateElement(selected.id, { fontId: event.target.value })}>
                    <option value="builtin-sans">{translate('ui.text.b119f1a8e4', language)}</option><option value="builtin-serif">{translate('ui.text.6538d5f26a', language)}</option><option value="builtin-mono">{translate('ui.text.d3ae4ad127', language)}</option>
                    {Object.values(document.fonts).map((font) => <option key={font.id} value={font.id}>{font.family} · lokalna</option>)}
                    {systemFonts.length > 0 && <optgroup label={`Pisave sistema (${systemFonts.length})`}>{systemFonts.map((family) => <option key={systemFontId(family)} value={systemFontId(family)}>{family}</option>)}</optgroup>}
                  </select></label>
                  <button type="button" className="toolbar-button" onClick={() => void importFont()}>{translate('ui.text.a0769fc134', language)}</button>
                  {fontMessage && <p className="import-message" role="status">{translateText(fontMessage, language)}</p>}
                  <div className="property-grid">
                    <label>{translate('ui.text.c44666a3eb', language)} <input type="number" min="1" max="2000" value={selected.fontSize} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value) && value > 0) updateElement(selected.id, { fontSize: value }); }} /></label>
                    <label>{translate('ui.text.9fdfb0db6d', language)} <select value={selected.fontWeight} onChange={(event) => updateElement(selected.id, { fontWeight: Number(event.target.value) })}><option value="300">{translate('ui.text.f24b13e93e', language)}</option><option value="400">{translate('ui.text.a56c3597eb', language)}</option><option value="600">{translate('ui.text.027bca5637', language)}</option><option value="700">{translate('ui.text.35b7b24203', language)}</option></select></label>
                    <label>{translate('ui.text.c79602b062', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={selected.fill} onChange={(event) => updateElement(selected.id, { fill: event.target.value })} /></label>
                    <label>{translate('ui.text.96739179e0', language)} <select value={selected.align} onChange={(event) => updateElement(selected.id, { align: event.target.value as 'left' | 'center' | 'right' })}><option value="left">{translate('ui.text.220d4518fc', language)}</option><option value="center">{translate('ui.text.d41c983eb2', language)}</option><option value="right">{translate('ui.text.bfbe0c758c', language)}</option></select></label>
                    <label>{translate('ui.text.717c7adc4c', language)} <input type="number" min="0.5" max="4" step="0.1" value={selected.lineHeight} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value) && value > 0) updateElement(selected.id, { lineHeight: value }); }} /></label>
                    <label>{translate('ui.text.49045bc986', language)} <input type="number" min="-20" max="100" step="1" value={selected.letterSpacing} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value)) updateElement(selected.id, { letterSpacing: value }); }} /></label>
                  </div>
                  <label className="checkbox-label"><input type="checkbox" checked={selected.italic} onChange={(event) => updateElement(selected.id, { italic: event.target.checked })} /> {translate('ui.text.2e3eaf5e49', language)}</label>
                  <div className="text-warp-controls">
                    <div className="panel-heading">{translate('ui.text.25c299d9bc', language)}</div>
                    <CommitRangeInput label={translate('ui.text.3b63679844', language)} min={-500} max={500} step={1} value={selected.curve} anchorValue={0} onCommit={(curve) => {
                      updateElement(selected.id, { curve, transform: { ...selected.transform, height: heightForCurveChange(selected, curve) } });
                    }} />
                    <CommitRangeInput label={translate('ui.text.1a8f2f4120', language)} min={-1.2} max={1.2} step={0.01} value={selected.slant} anchorValue={0} onCommit={(slant) => updateElement(selected.id, { slant })} />
                    <button type="button" className="toolbar-button" onClick={() => updateElement(selected.id, { transform: { ...selected.transform, height: heightForCurveChange(selected, selected.curve) } })}>{translate('ui.text.799c44e2ba', language)}</button>
                    <button type="button" className="toolbar-button" onClick={() => updateElement(selected.id, { curve: 0, slant: 0, transform: { ...selected.transform, height: heightForCurveChange(selected, 0) } })}>{translate('ui.text.e9bd9a3689', language)}</button>
                    <button type="button" className={`toolbar-button ${textWarpEditingId === selected.id ? 'is-active' : ''}`} onClick={() => setTextWarpEditing(textWarpEditingId === selected.id ? null : selected.id)}>{textWarpEditingId === selected.id ? translate('ui.text.198548905f', language) : translate('ui.text.b0fd1c3855', language)}</button>
                    <p className="panel-help">{translate('ui.text.f468857fcb', language)}</p>
                    <div className="panel-heading">{translate('ui.text.f28de4015c', language)}</div>
                    <label>{translate('ui.text.49117e15fd', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={selected.shadowColor} onChange={(event) => updateElement(selected.id, { shadowColor: event.target.value })} /></label>
                    <CommitRangeInput label={translate('ui.text.cfae0eec33', language)} min={0} max={60} value={selected.shadowBlur} formatValue={(value) => `${value} px`} onCommit={(shadowBlur) => updateElement(selected.id, { shadowBlur })} />
                    <div className="property-grid">
                      <label>{translate('ui.text.97a909f8c2', language)} <input type="number" min="-100" max="100" value={selected.shadowOffsetX} onChange={(event) => updateElement(selected.id, { shadowOffsetX: Number(event.target.value) || 0 })} /></label>
                      <label>{translate('ui.text.eca23bf3ba', language)} <input type="number" min="-100" max="100" value={selected.shadowOffsetY} onChange={(event) => updateElement(selected.id, { shadowOffsetY: Number(event.target.value) || 0 })} /></label>
                    </div>
                    <label>{translate('ui.text.fc20892778', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={selected.depthColor} onChange={(event) => updateElement(selected.id, { depthColor: event.target.value })} /></label>
                    <CommitRangeInput label={translate('ui.text.78a681bb3b', language)} min={0} max={30} step={1} value={selected.depth} formatValue={(value) => `${value} px`} onCommit={(depth) => updateElement(selected.id, { depth })} />
                  </div>
                </section>
              )}
              {selected.type === 'photo' && (
                <section className="crop-controls" aria-label={translate('ui.text.963428fb76', language)}>
                  <div className="panel-heading">{translate('ui.text.41fe0e87a3', language)}</div>
                  <div className="text-warp-controls">
                    <div className="panel-heading">{translate('ui.text.526445c2c3', language)}</div>
                    <div className="property-grid">
                      <label>{translate('ui.text.50140aec95', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={selected.frame.fill} onChange={(event) => updateElement(selected.id, { frame: { ...selected.frame, fill: event.target.value } })} /></label>
                      <label>{translate('ui.text.964bd6e692', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={selected.frame.borderColor} onChange={(event) => updateElement(selected.id, { frame: { ...selected.frame, borderColor: event.target.value } })} /></label>
                    </div>
                    <CommitRangeInput label={translate('ui.text.44b2b339b9', language)} min={0} max={Math.round(Math.min(selected.transform.width, selected.transform.height) / 2)} step={1} value={selected.frame.radius} formatValue={(value) => `${value} px`} onCommit={(radius) => updateElement(selected.id, { frame: { ...selected.frame, radius } })} />
                    <CommitRangeInput label={translate('ui.text.5147940031', language)} min={0} max={Math.round(Math.min(selected.transform.width, selected.transform.height) / 8)} step={1} value={selected.frame.borderWidth} formatValue={(value) => `${value} px`} onCommit={(borderWidth) => updateElement(selected.id, { frame: { ...selected.frame, borderWidth } })} />
                    <div className="panel-heading">{translate('ui.text.cb96633d47', language)}</div>
                    <div className="crop-fit-buttons">
                      <button type="button" className="toolbar-button" disabled={document.canvas.blackAndWhite} onClick={() => updateElement(selected.id, { imageSaturation: 0, imageBrightness: 1.05 })}>{translate('ui.text.29cc895133', language)}</button>
                      <button type="button" className="toolbar-button" disabled={document.canvas.blackAndWhite} onClick={() => updateElement(selected.id, { imageSaturation: 1.35, imageBrightness: 1.05, imageBlur: 0 })}>{translate('ui.text.0e884daf78', language)}</button>
                      <button type="button" className="toolbar-button" disabled={document.canvas.blackAndWhite} onClick={() => updateElement(selected.id, { imageSaturation: 0.72, imageBrightness: 1.08, imageBlur: 0 })}>{translate('ui.text.664330a071', language)}</button>
                      <button type="button" className="toolbar-button" disabled={document.canvas.blackAndWhite} onClick={() => updateElement(selected.id, { imageSaturation: 1, imageBrightness: 1, imageBlur: 0 })}>{translate('ui.text.392a3d9c00', language)}</button>
                    </div>
                  </div>
                  {selected.assetId && <>
                    <CommitRangeInput label={translate('ui.text.e23d484180', language)} min={0} max={100} value={Math.round(selected.imageOpacity * 100)} formatValue={(value) => `${value} %`} onCommit={(value) => updateElement(selected.id, { imageOpacity: value / 100 })} />
                    <CommitRangeInput label={translate('ui.text.a3cd98576b', language)} min={0} max={30} step={1} value={selected.imageBlur} formatValue={(value) => `${value} px`} onCommit={(imageBlur) => updateElement(selected.id, { imageBlur })} />
                    <CommitRangeInput label={translate('ui.text.33dd3a300e', language)} min={0} max={200} anchorValue={100} value={Math.round(selected.imageBrightness * 100)} formatValue={(value) => `${value} %`} onCommit={(value) => updateElement(selected.id, { imageBrightness: value / 100 })} />
                    <CommitRangeInput label={translate('ui.text.9ef61d160c', language)} disabled={document.canvas.blackAndWhite} min={0} max={200} anchorValue={100} value={Math.round(selected.imageSaturation * 100)} formatValue={(value) => `${value} %`} onCommit={(value) => updateElement(selected.id, { imageSaturation: value / 100 })} />
                  </>}
                  <div className="text-warp-controls">
                    <div className="panel-heading">{translate('ui.text.f372055e61', language)}</div>
                    <label>{translate('ui.text.49117e15fd', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={selected.shadowColor} onChange={(event) => updateElement(selected.id, { shadowColor: event.target.value })} /></label>
                    <CommitRangeInput label={translate('ui.text.cfae0eec33', language)} min={0} max={60} value={selected.shadowBlur} formatValue={(value) => `${value} px`} onCommit={(shadowBlur) => updateElement(selected.id, { shadowBlur })} />
                    <div className="property-grid">
                      <label>{translate('ui.text.a0cef3a20f', language)} <input type="number" min="-100" max="100" value={selected.shadowOffsetX} onChange={(event) => updateElement(selected.id, { shadowOffsetX: Number(event.target.value) || 0 })} /></label>
                      <label>{translate('ui.text.9487b05be0', language)} <input type="number" min="-100" max="100" value={selected.shadowOffsetY} onChange={(event) => updateElement(selected.id, { shadowOffsetY: Number(event.target.value) || 0 })} /></label>
                    </div>
                  </div>
                  <div className="crop-fit-buttons">
                    <button
                      type="button"
                      className={`toolbar-button ${selected.fit === 'cover' ? 'is-active' : ''}`}
                      onClick={() => updateElement(selected.id, { fit: 'cover', crop: { ...selected.crop, zoom: 1, offsetX: 0, offsetY: 0 } })}
                    >{translate('ui.text.bd67c3c04a', language)}</button>
                    <button
                      type="button"
                      className={`toolbar-button ${selected.fit === 'contain' ? 'is-active' : ''}`}
                      onClick={() => updateElement(selected.id, { fit: 'contain', crop: { ...selected.crop, zoom: 1, offsetX: 0, offsetY: 0 } })}
                    >{translate('ui.text.07d89efd75', language)}</button>
                  </div>
                  {selected.fit === 'cover' && (
                    <CommitRangeInput label={translate('ui.text.a03fcc3e66', language)} min={1} max={10} step={0.01} value={selected.crop.zoom} onCommit={(zoom) => updateElement(selected.id, { crop: { ...selected.crop, zoom } })} />
                  )}
                  <button type="button" className="toolbar-button" onClick={() => updateElement(selected.id, {
                    crop: { ...selected.crop, zoom: 1, offsetX: 0, offsetY: 0, quarterTurns: 0, flipX: false, flipY: false },
                  })}>{translate('ui.text.bce84b3847', language)}</button>
                  <div className="crop-transform-buttons">
                    <button type="button" className="toolbar-button" onClick={() => updateElement(selected.id, { crop: { ...selected.crop, quarterTurns: ((selected.crop.quarterTurns + 1) % 4) as 0 | 1 | 2 | 3, offsetX: 0, offsetY: 0, zoom: 1 } })}>{translate('ui.text.1d7e6a891e', language)}</button>
                    <button type="button" className="toolbar-button" onClick={() => updateElement(selected.id, { crop: { ...selected.crop, flipX: !selected.crop.flipX } })}>{translate('ui.text.6c7fb7baf6', language)}</button>
                    <button type="button" className="toolbar-button" onClick={() => updateElement(selected.id, { crop: { ...selected.crop, flipY: !selected.crop.flipY } })}>{translate('ui.text.0e24bf9c8f', language)}</button>
                  </div>
                  {selected.assetId && (cropEditingId === selected.id ? (
                    <div className="crop-edit-actions">
                      <p>{translate('ui.text.d1e0765d17', language)}</p>
                      <button type="button" className="import-button" onClick={() => finishCropEdit(true)}>{translate('ui.text.4bb4a3ea5b', language)}</button>
                      <button type="button" className="toolbar-button" onClick={() => finishCropEdit(false)}>{translate('ui.text.1c221cdd8c', language)}</button>
                    </div>
                  ) : (
                    <button type="button" className="import-button" onClick={startCropEdit}>{translate('ui.text.881eb02445', language)}</button>
                  ))}
                </section>
              )}
            </div>
          ) : (
            <div className="property-list canvas-properties">
              <div className="panel-heading">{translate('ui.text.74fdcec7b3', language)}</div>
              <label>{translate('ui.text.187b47c833', language)} <select value={Object.entries(CANVAS_PRESETS).find(([, preset]) =>
                document.canvas.width === preset.width && document.canvas.height === preset.height && (preset.ppi === undefined || document.canvas.ppi === preset.ppi),
              )?.[0] ?? 'custom'} onChange={(event) => {
                const preset = CANVAS_PRESETS[event.target.value];
                if (preset) resizeCanvas(preset.width, preset.height, preset.ppi);
              }}>
                <option value="custom" disabled>{translate('ui.text.84793f02dc', language)}</option><option value="square">{translate('ui.text.e167f843b1', language)}</option><option value="landscape">{translate('ui.text.605aaef653', language)}</option><option value="portrait">{translate('ui.text.2eda570019', language)}</option><option value="hd">{translate('ui.text.90a5959dd7', language)}</option><option value="story">{translate('ui.text.d034baf8a8', language)}</option><option value="a4p">{translate('ui.text.45571bfda9', language)}</option><option value="a4l">{translate('ui.text.1b23298e24', language)}</option>
              </select></label>
              <div className="property-grid">
                <label>{translate('ui.text.ff0ffd4fd3', language)} <input type="number" min="64" max="8192" value={canvasWidthInput} onFocus={beginTransaction} onBlur={() => { resizeCanvas(Number(canvasWidthInput), document.canvas.height); commitTransaction(); }} onChange={(event) => setCanvasWidthInput(event.target.value)} /></label>
                <label>{translate('ui.text.9b4f244d5e', language)} <input type="number" min="64" max="8192" value={canvasHeightInput} onFocus={beginTransaction} onBlur={() => { resizeCanvas(document.canvas.width, Number(canvasHeightInput)); commitTransaction(); }} onChange={(event) => setCanvasHeightInput(event.target.value)} /></label>
              </div>
              <label>{translate('ui.text.2d6f3298b2', language)} <input type="number" min="36" max="1200" value={canvasPpiInput} onFocus={beginTransaction} onBlur={() => { const value = Number(canvasPpiInput); if (Number.isFinite(value) && value >= 36 && value <= 1200) updateCanvas({ ppi: value }); commitTransaction(); }} onChange={(event) => setCanvasPpiInput(event.target.value)} /></label>
              <label className="checkbox-label"><input type="checkbox" checked={document.canvas.blackAndWhite} onChange={(event) => updateCanvas({ blackAndWhite: event.target.checked })} /> {translate('ui.text.6863eaa34d', language)}</label>
              <label>{translate('ui.text.38231b47b6', language)} <input type="color" disabled={document.canvas.blackAndWhite} value={document.canvas.background ?? '#FFFFFF'} onChange={(event) => updateCanvas({ background: event.target.value })} /></label>
              <label>{translate('ui.text.fb41bb9d3c', language)} <select value={document.canvas.backgroundAssetId ?? ''} onChange={(event) => updateCanvas({ backgroundAssetId: event.target.value || null })}>
                <option value="">{translate('ui.text.cef567a01c', language)}</option>
                {Object.values(document.assets).map((asset) => <option key={asset.id} value={asset.id}>{asset.originalName}</option>)}
              </select></label>
              {document.canvas.backgroundAssetId && <>
                <CommitRangeInput label={translate('ui.text.bfab35dae8', language)} min={0} max={100} value={Math.round(document.canvas.backgroundImageOpacity * 100)} formatValue={(value) => `${value} %`} onCommit={(value) => updateCanvas({ backgroundImageOpacity: value / 100 })} />
                <CommitRangeInput label={translate('ui.text.6463d985de', language)} min={-100} max={100} anchorValue={0} value={document.canvas.backgroundImageOffsetX} formatValue={(value) => `${value} %`} onCommit={(backgroundImageOffsetX) => updateCanvas({ backgroundImageOffsetX })} />
                <CommitRangeInput label={translate('ui.text.239bf9e401', language)} min={-100} max={100} anchorValue={0} value={document.canvas.backgroundImageOffsetY} formatValue={(value) => `${value} %`} onCommit={(backgroundImageOffsetY) => updateCanvas({ backgroundImageOffsetY })} />
                <CommitRangeInput label={translate('ui.text.b8caf7d201', language)} min={0} max={30} step={1} value={document.canvas.backgroundImageBlur} formatValue={(value) => `${value} px`} onCommit={(backgroundImageBlur) => updateCanvas({ backgroundImageBlur })} />
                <CommitRangeInput label={translate('ui.text.92b4d74be5', language)} min={0} max={200} anchorValue={100} value={Math.round(document.canvas.backgroundImageBrightness * 100)} formatValue={(value) => `${value} %`} onCommit={(value) => updateCanvas({ backgroundImageBrightness: value / 100 })} />
                <CommitRangeInput label={translate('ui.text.d77cac413f', language)} disabled={document.canvas.blackAndWhite} min={0} max={200} anchorValue={100} value={Math.round(document.canvas.backgroundImageSaturation * 100)} formatValue={(value) => `${value} %`} onCommit={(value) => updateCanvas({ backgroundImageSaturation: value / 100 })} />
              </>}
              <p className="panel-help">{translate('ui.text.e2a84f6e9a', language)}</p>
            </div>
          )}
        </aside>
      </div>

      <footer className="statusbar">
        <span>{document.canvas.width} × {document.canvas.height} px</span>
        <span>{selected ? selected.name : translate('status.nothingSelected', language)}</span>
        <a className="support-project" href="https://www.paypal.com/donate/?hosted_button_id=4BS9ZUXJ2P7GN" onClick={(event) => { event.preventDefault(); void window.photoCollage.openExternal('https://www.paypal.com/donate/?hosted_button_id=4BS9ZUXJ2P7GN'); }}>{translate('status.support', language)}</a>
        <span className="status-state">{isDirty ? translate('status.unsaved', language) : translate('status.saved', language)}</span>
        <button type="button" className="language-toggle" onClick={() => { const next = language === 'sl' ? 'en' : 'sl'; languageChangedRef.current = true; setLanguage(next); saveLanguage(next); void window.photoCollage.setLanguagePreference(next).catch(() => undefined); }} aria-label={translate('language.switch', language)}>{language.toUpperCase()}</button>
      </footer>
      {exportOpen && <div className="modal-backdrop"><section className="export-dialog" role="dialog" aria-modal="true" aria-labelledby="export-title">
        <div className="panel-heading" id="export-title">{translate('ui.text.7fabad796a', language)}</div>
        <label>{translate('ui.text.041a5dec48', language)} <select value={exportFormat} onChange={(event) => setExportFormat(event.target.value as 'png' | 'jpeg')}><option value="png">{translate('ui.text.2463fb629d', language)}</option><option value="jpeg">{translate('ui.text.fbedcaab10', language)}</option></select></label>
        <div className="property-grid"><label>{translate('ui.text.4f25bf6420', language)} <input type="number" min="1" max="32767" value={exportWidth} onChange={(event) => setExportWidth(event.target.value)} /></label><label>{translate('ui.text.ba8da3e2cf', language)} <input type="number" min="1" max="32767" value={exportHeight} onChange={(event) => setExportHeight(event.target.value)} /></label></div>
        <label>{translate('ui.text.94dad90bf8', language)} <select value={exportScale} onChange={(event) => setExportScale(Number(event.target.value) as 1 | 2 | 3 | 4)}><option value={1}>{translate('ui.text.5228105cf6', language)}</option><option value={2}>2×</option><option value={3}>3×</option><option value={4}>4×</option></select></label>
        <p className="panel-help">{formatMessage('msg.exportDimensions', { width: Number(exportWidth) * exportScale || 0, height: Number(exportHeight) * exportScale || 0 }, language)}</p>
        <div className="export-actions"><button type="button" className="toolbar-button" onClick={() => setExportOpen(false)} disabled={exporting}>{translate('ui.text.1c221cdd8c', language)}</button><button type="button" className="toolbar-button primary-action" onClick={() => void exportCurrentImage()} disabled={exporting || !Number.isInteger(Number(exportWidth)) || !Number.isInteger(Number(exportHeight)) || Number(exportWidth) < 1 || Number(exportHeight) < 1 || Number(exportWidth) * exportScale > 32767 || Number(exportHeight) * exportScale > 32767 || Number(exportWidth) * Number(exportHeight) * exportScale * exportScale > 256_000_000}> {exporting ? translate('ui.text.5f853ae5f3', language) : translate('ui.text.155c6beb4b', language)}</button></div>
      </section></div>}
    </main>
  );
}

export default App;
